import {
  CreateParams,
  CreateResult,
  DataProvider,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  UpdateParams,
  UpdateResult,
} from 'react-admin'
import { FILES_URL } from '../api-urls'
import {
  get,
  getBlob,
  getWithPagination,
  patch,
  postFile,
} from '../common/fetch.utils'
import {
  filterParamsComposer,
  queryParamsComposer,
} from '../common/get-by-conditions.utils'
import { TicketAPIGetListParams } from '../common/ticket-api-get-list.params'
import { RecurringJobCreationParamsDto } from '../dto/recurring-job-creation-params.dto'
import { FilePathDto } from '../dto/files/FilesPath.dto'
import filterMapper from './file-filter.mapper'
import { mapSortFileParam } from './file-sort.mapper'

const provider = {
  getList: async (
    resource: string,
    { filter, sort, pagination }: TicketAPIGetListParams,
  ): Promise<GetListResult<FilePathDto>> => {
    const filterParams = `e=>${filterParamsComposer('e', filter, filterMapper)}`
    const pathParams = queryParamsComposer(sort, pagination, mapSortFileParam)
    const path = `/${filterParams}${(pathParams && `?${pathParams}`) || ''}`

    const {
      data,
      range: { total },
    } = await getWithPagination<FilePathDto[]>(
      `${FILES_URL}/GetByConditions`,
      path,
    )

    return Promise.resolve({
      data,
      total,
    })
  },
  getOne: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult<FilePathDto>> => {
    const data = await get<FilePathDto>(FILES_URL, `/${id}`)

    return {
      data,
    }
  },
  create: async (
    resource: string,
    { data }: CreateParams<CreateFileRequest>,
  ): Promise<CreateResult<FilePathDto>> => {
    const { name, file } = data
    const body = new FormData()
    body.append('File', file?.rawFile as File)
    if (name) body.append('NameOverride', name)
    const create = await postFile<FilePathDto[]>(FILES_URL, body)
    return Promise.resolve({ data: create[0] })
  },
  update: async (
    resource: string,
    { id, data, previousData }: UpdateParams<FilePathDto>,
  ): Promise<UpdateResult<FilePathDto>> => {
    const update = await patch<
      { id: Identifier; [n: string]: any },
      FilePathDto
    >(FILES_URL, {
      id,
      path: data.path !== previousData.path ? data.path : undefined,
      name: data.name !== previousData.name ? data.name : undefined,
    })
    return Promise.resolve({ data: update })
  },
  getMany: async (
    resource: string,
    { ids }: GetManyParams,
  ): Promise<GetManyResult<FilePathDto>> => {
    const data = await get<FilePathDto[]>(
      FILES_URL,
      `/GetByConditions/e=>new int[] {${ids.toString()}}.Contains(e.Id)`,
    )
    return Promise.resolve({ data })
  },
  downloadFile: async (
    resource: string,
    { id }: GetOneParams,
  ): Promise<GetOneResult> => {
    const blob = await getBlob(FILES_URL, `/DownloadFile/${id}`)

    return Promise.resolve({
      data: {
        id,
        blob,
      },
    })
  },
} as FileDataProvider

interface FileDataProvider extends DataProvider {
  downloadFile: (
    resource: string,
    params: GetOneParams,
  ) => Promise<GetOneResult>
}

export interface ChangeStateParams extends RecurringJobCreationParamsDto {
  eventId: Identifier
}

export interface ChangeDevicesStateParams extends ChangeStateParams {
  readonly verifierDevicesIds: number[]
}

export interface ChangeStateResponse {
  jobId: Identifier
}

export interface GetCompaniesParams {
  eventId: Identifier
}

export interface GetObjectsParams {
  eventId: Identifier
}

export interface GetDevicesParams {
  eventId: Identifier
}

export interface CreateFileRequest {
  readonly name: string
  readonly file?: {
    title: string
    src: string
    rawFile: File
  }
}

export default provider
